import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Star, CheckCircle, Storefront, ArrowLeft, Globe, FacebookLogo, InstagramLogo, MagnifyingGlass } from "@phosphor-icons/react";
import { useSiteContent } from "../lib/useSiteContent";
import { useLanguage } from "../hooks/useLanguage";
import LanguageToggle from "../components/LanguageToggle";
import { OuterNav } from "../layouts/PublicNav";
import { ScoreBadge, ReviewCount } from "../components/ScoreBadge";
import Logo from "../components/Logo";
import { useAuth } from "../lib/auth";

const AGE_BANDS = ["18-24", "25-34", "35-44", "45-54", "55+"];
const GENDERS = ["Male", "Female", "Other"];

function useT() {
  const content = useSiteContent();
  return (k, fallback) => (content[k] && String(content[k]).trim() ? content[k] : fallback);
}

// ---------------------------------------------------------------------------
// Silent sales-demo auto-login — oodel.co/:a/:b/demo (e.g. /eatology/877/demo).
// No email/password prompt: the 2 URL segments ARE the secret key, matched
// server-side against a business's `demo_login_key`. Lands straight on the
// business dashboard so a prospect just clicks a link. See POST
// /api/auth/demo-login in backend/routers/auth.py.
// ---------------------------------------------------------------------------
export function DemoAutoLogin() {
  const { a, b } = useParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    api.post("/auth/demo-login", { key: `${a}/${b}` })
      .then(({ data }) => {
        localStorage.setItem("pollpak_token", data.token);
        setUser(data.user);
        navigate("/business", { replace: true });
      })
      .catch(() => setError("This demo link is invalid or has expired. Please contact sales@oodel.co for a fresh one."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a, b]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-6" data-testid="demo-login-error">
        <div className="text-center max-w-sm">
          <Logo to={false} size="md" className="justify-center mb-4" />
          <p className="text-sm text-neutral-500">{error}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50" data-testid="demo-login-loading">
      <div className="text-center">
        <Logo to={false} size="md" className="justify-center mb-3" />
        <p className="text-sm text-neutral-400">Loading your demo dashboard…</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QR entry point — /scan/:qrCode (what's physically encoded in the QR image)
// and /rate/:qrCode (legacy alias, for QR codes generated before this
// token-gated flow existed). Landing here IS the scan: mint a short-lived
// token server-side, then redirect to /feedback/:qrCode?t=... — the actual
// form. Anyone who skips this hop (types/shares/bookmarks the bare
// /feedback/:qrCode URL) has no token and can't submit — see
// CustomerSentimentRate below.
// ---------------------------------------------------------------------------
export function CustomerSentimentScan() {
  const { qrCode } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.post(`/cs/scan-token/${qrCode}`)
      .then((r) => navigate(`/feedback/${qrCode}?t=${r.data.token}`, { replace: true }))
      .catch((e) => { setStatus(e.response?.status || null); setError(e.response?.data?.detail || "This QR code isn't active right now."); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode]);

  if (status === 423) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]" data-testid="cs-scan-locked">
        <Logo to={false} size="lg" />
      </div>
    );
  }
  if (status === 402) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6" data-testid="cs-scan-suspended">
        <div className="max-w-sm text-center" dir={lang === "ur" ? "rtl" : "ltr"}>
          <Storefront size={40} className="mx-auto text-neutral-400 mb-3" />
          <h1 className="text-lg font-bold tracking-tight mb-1.5">{t("ui_ur_cs_suspended_title")}</h1>
          <p className="text-sm text-neutral-500">{t("ui_ur_cs_suspended_message")}</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6" data-testid="cs-scan-error">
        <div className="max-w-sm text-center">
          <Storefront size={40} className="mx-auto text-neutral-400 mb-3" />
          <p className="text-neutral-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]" data-testid="cs-scan-loading">
      <div className="text-xs uppercase tracking-widest text-neutral-400">Loading…</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public one-screen QR rating form — /feedback/:qrCode?t=... (token minted
// by CustomerSentimentScan above; missing/invalid token = blocked, see
// `noToken` state).
// ---------------------------------------------------------------------------
export function CustomerSentimentRate() {
  const { qrCode } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || "";
  const previewToken = searchParams.get("preview") || "";
  const isPreview = !!previewToken;
  const { lang, t } = useLanguage();
  const content = useSiteContent();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [loadStatus, setLoadStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [lookupDone, setLookupDone] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const emailRef = useRef("");
  const [ratings, setRatings] = useState({});
  const [hover, setHover] = useState({});
  const [ageBand, setAgeBand] = useState("");
  const [gender, setGender] = useState("");
  const consent = true; // mandatory, always-on — see consent checkbox below
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [textAnswers, setTextAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const fetchStartedRef = useRef(false);

  useEffect(() => {
    if (isPreview) {
      // Admin preview — bypasses the single-use scan-token flow entirely,
      // re-fetchable for the full 2h window (no sessionStorage caching, no
      // fetchStartedRef guard needed since the backend token isn't burned).
      api.get(`/cs/preview/${qrCode}`, { params: { pt: previewToken } })
        .then((r) => setInfo(r.data))
        .catch((e) => { setLoadStatus(e.response?.status || null); setLoadError(e.response?.data?.detail || "This preview link isn't active right now."); })
        .finally(() => setLoading(false));
      return;
    }
    if (!token) { setLoadStatus(401); setLoading(false); return; }
    // Guards against React StrictMode's dev-only double-invoke of this
    // effect (mount → cleanup → mount again on the SAME instance) — without
    // this, two real requests fire for one page load and the single-use
    // token gets burned by our own second call before the visitor even sees
    // the form. A ref survives that synthetic re-run, a plain variable
    // wouldn't.
    if (fetchStartedRef.current) return;
    fetchStartedRef.current = true;
    // The form data for a given token is cached client-side the first time
    // it loads successfully — a refresh in THIS tab reuses the cache
    // instead of re-hitting the backend (which marks the token used on
    // first load, so a genuinely different browser/tab opening the same
    // copied link gets a clear "already used" message instead of the form).
    const cacheKey = `cs_feedback_${token}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) { setInfo(JSON.parse(cached)); setLoading(false); return; }
    } catch { /* sessionStorage unavailable — fall through to network */ }
    api.get(`/cs/scan/${qrCode}`, { params: { t: token } })
      .then((r) => {
        setInfo(r.data);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(r.data)); } catch {}
      })
      .catch((e) => { setLoadStatus(e.response?.status || null); setLoadError(e.response?.data?.detail || "This QR code isn't active right now."); })
      .finally(() => setLoading(false));
  }, [qrCode, token, isPreview, previewToken]);

  const onEmailBlur = async () => {
    if (!email || lookupDone || !/^\S+@\S+\.\S+$/.test(email)) return;
    setLookupDone(true);
    const lookedUpEmail = email;
    try {
      const r = await api.get("/cs/lookup", { params: { email: lookedUpEmail } });
      // The lookup is async and can resolve AFTER the customer has already
      // edited the email again (fast typing/pasting, or a slow connection)
      // — applying a stale response at that point would silently overwrite
      // whatever they've since typed. Only apply it if the email field
      // still holds the exact address we looked up.
      if (r.data.found && emailRef.current === lookedUpEmail) {
        setAgeBand(r.data.age_band || "");
        setGender(r.data.gender || "");
        setPrefilled(true);
        toast.success("Welcome back — we've pre-filled your details.");
      }
    } catch { /* silent — non-critical */ }
  };

  const onEmailChange = (e) => {
    const val = e.target.value;
    emailRef.current = val;
    setEmail(val);
    // The age/gender currently shown were auto-fetched for the PREVIOUS
    // email — once that email is edited/cleared they no longer belong to
    // whatever's now in the field, so wipe them silently and let a fresh
    // blur re-fetch (or the customer fill them in manually).
    if (prefilled) {
      setAgeBand("");
      setGender("");
      setPrefilled(false);
      setLookupDone(false);
    }
  };

  const questions = info?.template?.questions || [];
  const questionText = (q) => (lang === "ur" && q.text_ur ? q.text_ur : q.text);
  const starQuestions = questions.filter((q) => (q.type || "star") !== "open_ended");
  const allRated = starQuestions.length > 0 && starQuestions.every((q) => ratings[q.id]);

  const submit = async () => {
    if (isPreview) { toast.error("Preview mode — submissions are disabled. This is just to check how the form looks."); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("Please enter a valid email."); return; }
    if (!allRated) { toast.error("Please rate every question."); return; }
    if (!ageBand || !gender) { toast.error("Please complete your age & gender."); return; }
    setSubmitting(true);
    try {
      // The mandatory consent checkbox covers BOTH sharing feedback with
      // the business AND oodel emailing them a verify/set-password link —
      // it's always on and can't be unchecked, so no client-side gate is
      // needed here. City isn't asked here at all — it's set once by admin
      // on the location/QR itself (see LocationsCard).
      const r = await api.post(`/cs/scan/${qrCode}/submit`, {
        email, ratings, age_band: ageBand, gender, consent, marketing_opt_in: marketingOptIn,
        create_account: true, text_answers: textAnswers,
      }, { params: { t: token } });
      setSubmitted(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]" data-testid="cs-rate-loading"><div className="text-xs uppercase tracking-widest text-neutral-400">Loading…</div></div>;
  }
  if (loadStatus === 401) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6" data-testid="cs-rate-no-token">
        <div className="max-w-sm text-center" dir={lang === "ur" ? "rtl" : "ltr"}>
          <Storefront size={40} className="mx-auto text-neutral-400 mb-3" />
          <p className="text-neutral-600 font-medium">{t("ui_ur_cs_no_token_message")}</p>
        </div>
      </div>
    );
  }
  if (loadStatus === 410) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6" data-testid="cs-rate-link-used">
        <div className="max-w-sm text-center">
          <Storefront size={40} className="mx-auto text-neutral-400 mb-3" />
          <h1 className="text-lg font-bold tracking-tight mb-1.5">{t("ui_ur_cs_link_used_title")}</h1>
          <p className="text-sm text-neutral-500">{t("ui_ur_cs_link_used_message")}</p>
        </div>
      </div>
    );
  }
  if (loadStatus === 423) {
    // Locked — deliberately minimal: just the oodel logo, nothing else.
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]" data-testid="cs-rate-locked">
        <Logo to={false} size="lg" />
      </div>
    );
  }
  if (loadStatus === 402) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6" data-testid="cs-rate-suspended">
        <div className="max-w-sm text-center" dir={lang === "ur" ? "rtl" : "ltr"}>
          <Storefront size={40} className="mx-auto text-neutral-400 mb-3" />
          <h1 className="text-lg font-bold tracking-tight mb-1.5">{t("ui_ur_cs_suspended_title")}</h1>
          <p className="text-sm text-neutral-500">{t("ui_ur_cs_suspended_message")}</p>
          <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-neutral-400">{t("ui_ur_cs_powered_by")} {content.cs_powered_by_logo_url ? <img src={content.cs_powered_by_logo_url} alt="" className="h-6 object-contain" style={{ maxWidth: 130 }} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Logo to={false} size="md" />}</div>
        </div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6" data-testid="cs-rate-error">
        <div className="max-w-sm text-center">
          <Storefront size={40} className="mx-auto text-neutral-400 mb-3" />
          <p className="text-neutral-600 font-medium">{loadError}</p>
        </div>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-6" data-testid="cs-rate-thankyou">
        <div className="max-w-sm w-full bg-white rounded-3xl border border-neutral-200 shadow-xl p-8 text-center">
          <CheckCircle size={52} weight="fill" className="mx-auto text-emerald-500 mb-4" />
          <h1 className="text-2xl font-black tracking-tight mb-2">{t("ui_ur_cs_thank_you_title")}</h1>
          <p className="text-sm text-neutral-500">{t("ui_ur_cs_thank_you_message")} <b>{info.business_name}</b>.</p>
          {submitted.account_created && (
            <p className="text-xs text-emerald-600 font-medium mt-3 bg-emerald-50 rounded-xl p-3" data-testid="cs-rate-account-created-note">
              {t("ui_ur_cs_account_created_note")}
            </p>
          )}
          <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-neutral-400">{t("ui_ur_cs_powered_by")} {content.cs_powered_by_logo_url ? <img src={content.cs_powered_by_logo_url} alt="" className="h-6 object-contain" style={{ maxWidth: 130 }} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span className="font-black text-xl" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span className="text-mango">.</span></span>}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-4 py-10" data-testid="cs-rate-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-neutral-200 shadow-xl p-6 md:p-8">
        {isPreview && (
          <div className="mb-4 -mt-1 rounded-xl bg-black text-white text-xs font-bold uppercase tracking-wide px-3 py-2 text-center" data-testid="cs-rate-preview-banner">
            Admin preview — submissions are disabled
          </div>
        )}
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex items-center gap-3.5">
            {info.logo_url ? (
              <img src={info.logo_url} alt="" className="w-16 h-16 rounded-2xl object-cover border border-neutral-200 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="cs-rate-business-logo" />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-mango/10 text-mango flex items-center justify-center shrink-0"><Storefront size={30} weight="fill" /></div>
            )}
            <div>
              <div className="font-black text-lg leading-tight">{info.business_name}</div>
              {info.location_name && <div className="text-xs text-neutral-400">{info.location_name}</div>}
            </div>
          </div>
          <div className="flex flex-col items-end shrink-0 gap-1.5">
            {info.show_urdu_version && <LanguageToggle variant="score" />}
            <div className="flex flex-col items-end pt-0.5" data-testid="cs-rate-powered-by">
              <span className="text-[9px] font-semibold uppercase tracking-wide text-neutral-400 mb-0.5">{t("ui_ur_cs_powered_by")}</span>
              {content.cs_powered_by_logo_url ? <img src={content.cs_powered_by_logo_url} alt="" className="h-6 object-contain" style={{ maxWidth: 130 }} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Logo to={false} size="md" />}
            </div>
          </div>
        </div>
        <h1 className="text-xl font-bold tracking-tight mt-4 mb-1" dir={lang === "ur" ? "rtl" : "ltr"}>{t("ui_ur_cs_rate_title")}</h1>
        <p className="text-sm text-neutral-500 mb-5" dir={lang === "ur" ? "rtl" : "ltr"}>{t("ui_ur_cs_rate_subtitle")}</p>

        <div className="space-y-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-1 block" dir={lang === "ur" ? "rtl" : "ltr"}>{t("ui_ur_cs_email_label")}</label>
            <Input type="email" data-testid="cs-rate-email" value={email} onChange={onEmailChange} onBlur={onEmailBlur} placeholder={t("ui_ur_cs_email_placeholder")} className="rounded-xl" />
          </div>

          {questions.map((q) => (
            <div key={q.id} data-testid={`cs-rate-question-${q.id}`}>
              <label className="text-sm font-semibold text-neutral-800 mb-1.5 block" dir={lang === "ur" && q.text_ur ? "rtl" : "ltr"}>{questionText(q)}{(q.type || "star") === "open_ended" && <span className="text-neutral-400 font-normal"> (optional)</span>}</label>
              {(q.type || "star") === "open_ended" ? (
                <Textarea
                  data-testid={`cs-rate-open-${q.id}`} value={textAnswers[q.id] || ""}
                  onChange={(e) => setTextAnswers((ta) => ({ ...ta, [q.id]: e.target.value }))}
                  dir={lang === "ur" ? "rtl" : "ltr"} rows={3} maxLength={2000}
                  className="rounded-xl border-2 border-neutral-300 focus-visible:border-black resize-none"
                />
              ) : (
                <div className="flex items-center gap-1.5" onMouseLeave={() => setHover((h) => ({ ...h, [q.id]: 0 }))}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`cs-rate-${q.id}-star-${s}`}
                      onMouseEnter={() => setHover((h) => ({ ...h, [q.id]: s }))}
                      onClick={() => setRatings((r) => ({ ...r, [q.id]: s }))}
                      className={`text-3xl transition hover:scale-110 ${(hover[q.id] ? hover[q.id] >= s : (ratings[q.id] || 0) >= s) ? "text-mango" : "text-neutral-300"}`}
                      aria-label={`${s} star`}
                    >
                      <Star weight="fill" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-1 block" dir={lang === "ur" ? "rtl" : "ltr"}>{t("ui_ur_cs_age_label")}</label>
              <select data-testid="cs-rate-age" value={ageBand} onChange={(e) => setAgeBand(e.target.value)} dir={lang === "ur" ? "rtl" : "ltr"} className="w-full px-3 py-2 border-2 border-neutral-300 rounded-xl text-sm bg-white focus:outline-none focus:border-black">
                <option value="">{t("ui_ur_cs_select_placeholder")}</option>
                {AGE_BANDS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-1 block" dir={lang === "ur" ? "rtl" : "ltr"}>{t("ui_ur_cs_gender_label")}</label>
              <select data-testid="cs-rate-gender" value={gender} onChange={(e) => setGender(e.target.value)} dir={lang === "ur" ? "rtl" : "ltr"} className="w-full px-3 py-2 border-2 border-neutral-300 rounded-xl text-sm bg-white focus:outline-none focus:border-black">
                <option value="">{t("ui_ur_cs_select_placeholder")}</option>
                {GENDERS.map((g) => <option key={g} value={g}>{t(`ui_ur_cs_gender_${g.toLowerCase()}`)}</option>)}
              </select>
            </div>
          </div>
          <label className="flex items-start gap-2.5" data-testid="cs-rate-consent-row">
            <Checkbox checked disabled data-testid="cs-rate-consent" className="mt-0.5" />
            <span className="text-xs text-neutral-500" dir={lang === "ur" ? "rtl" : "ltr"}>
              {t("ui_ur_cs_consent_text").replace("{business_name}", info?.business_name || "")}
            </span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="cs-rate-marketing-optin-row">
            <Checkbox checked={marketingOptIn} onCheckedChange={(v) => setMarketingOptIn(!!v)} data-testid="cs-rate-marketing-optin" className="mt-0.5" />
            <span className="text-xs text-neutral-500" dir={lang === "ur" ? "rtl" : "ltr"}>
              {t("ui_ur_cs_marketing_optin_text").replace("{business_name}", info?.business_name || "")}
            </span>
          </label>

          <Button onClick={submit} disabled={submitting || isPreview} data-testid="cs-rate-submit" className="w-full rounded-full bg-mango hover:bg-mango/90 text-white h-12 font-bold disabled:opacity-50">
            {isPreview ? "Preview only — submit disabled" : submitting ? t("ui_ur_cs_submitting_label") : t("ui_ur_cs_submit_label")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public business profile page — /cs/:businessSlug
// ---------------------------------------------------------------------------
export function CustomerSentimentBadge() {
  const { businessSlug } = useParams();
  const T = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/cs/badge/${businessSlug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Business not found."))
      .finally(() => setLoading(false));
  }, [businessSlug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0B1424]" data-testid="cs-badge-loading" />;
  if (error) return (
    <div className="min-h-screen bg-[#0B1424]"><OuterNav />
      <div className="flex items-center justify-center text-white/60 py-24" data-testid="cs-badge-error">{error}</div>
    </div>
  );

  const socials = [
    data.website_url && { icon: Globe, href: data.website_url, label: "Website" },
    data.facebook_url && { icon: FacebookLogo, href: data.facebook_url, label: "Facebook" },
    data.instagram_url && { icon: InstagramLogo, href: data.instagram_url, label: "Instagram" },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0B1424]" data-testid="cs-badge-page">
      <OuterNav />
      <div className="max-w-3xl mx-auto px-6 py-14 text-center">
        <Link to="/oodelscore" className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-white/70 mb-8" data-testid="cs-back-to-directory">
          <ArrowLeft size={13} /> {T("cs_back_to_directory", "Back to oodel SCORE directory")}
        </Link>
        {data.logo_url ? (
          <img src={data.logo_url} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-2 border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="w-20 h-20 rounded-full bg-[#E9C767]/10 text-[#E9C767] flex items-center justify-center mx-auto mb-4"><Storefront size={34} weight="fill" /></div>
        )}
        <h1 className="text-3xl font-black tracking-tight text-white" data-testid="cs-badge-business-name">{data.business_name}</h1>
        {data.industry && <div className="text-sm text-white/40 mt-1">{data.industry}</div>}
        {socials.length > 0 && (
          <div className="flex items-center justify-center gap-4 mt-4">
            {socials.map((s, i) => (
              <a key={i} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`cs-profile-social-${s.label.toLowerCase()}`} className="text-white/40 hover:text-[#E9C767] transition-colors"><s.icon size={22} /></a>
            ))}
          </div>
        )}
        <div className="flex flex-col items-center gap-3 my-10">
          <ScoreBadge avgStars={data.avg_stars} size="lg" testid="cs-profile-score-badge" />
          <ReviewCount totalResponses={data.total_responses} />
        </div>
        {data.branches && data.branches.length > 0 && (
          <div className="mt-4" data-testid="cs-profile-branches">
            <div className="overline text-white/30 mb-5">BY LOCATION</div>
            <div className="flex flex-wrap justify-center gap-8">
              {data.branches.map((b, i) => (
                <div key={i} className="flex flex-col items-center gap-2" data-testid={`cs-profile-branch-${i}`}>
                  <ScoreBadge avgStars={b.avg_stars} size="sm" testid={`cs-profile-branch-badge-${i}`} />
                  <div className="text-xs font-bold text-white/70">{b.name}</div>
                  <ReviewCount totalResponses={b.total_responses} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Get your business listed" lead-capture modal — posts to /cs/contact,
// which the backend forwards to info@oodel.co.
// ---------------------------------------------------------------------------
function LeadFormModal({ open, onClose }) {
  const T = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email) || !form.message.trim()) {
      toast.error("Please fill in your name, a valid email, and a message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/cs/contact", form);
      setSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send. Please try again.");
    } finally { setSending(false); }
  };

  const close = () => { setSent(false); setForm({ name: "", email: "", phone: "", message: "" }); onClose(); };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="cs-lead-form-modal">
      <div className="bg-[#0B1424] border border-[#E9C767]/30 rounded-3xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="text-center py-6">
            <CheckCircle size={44} weight="fill" className="mx-auto text-[#E9C767] mb-4" />
            <h3 className="text-xl font-black text-white mb-2">{T("cs_lead_form_success_title", "Thanks — we'll be in touch!")}</h3>
            <p className="text-sm text-white/50 mb-6">{T("cs_lead_form_success_message", "Our team will reach out shortly to get your business listed on oodel SCORE.")}</p>
            <Button onClick={close} className="rounded-full bg-[#E9C767] hover:bg-[#dcb955] text-[#0B1424] font-bold" data-testid="cs-lead-close-success">Close</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-xl font-black text-white">{T("cs_lead_form_title", "Get your business listed")}</h3>
              <button onClick={close} data-testid="cs-lead-close" className="text-white/30 hover:text-white text-xl leading-none">×</button>
            </div>
            <p className="text-sm text-white/40 mb-5">{T("cs_lead_form_subtitle", "Tell us a bit about your business and we'll set you up on oodel SCORE.")}</p>
            <div className="space-y-3">
              <Input value={form.name} onChange={set("name")} placeholder="Your name" data-testid="cs-lead-name" className="rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              <Input type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="cs-lead-email" className="rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              <Input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" data-testid="cs-lead-phone" className="rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30" />
              <textarea value={form.message} onChange={set("message")} placeholder="Tell us about your business…" rows={4} data-testid="cs-lead-message" className="w-full rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-3 py-2 text-sm focus:outline-none focus:border-[#E9C767]/50" />
            </div>
            <Button onClick={submit} disabled={sending} className="w-full mt-5 rounded-full bg-[#E9C767] hover:bg-[#dcb955] text-[#0B1424] h-11 font-bold" data-testid="cs-lead-submit">
              {sending ? "Sending…" : "Submit"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// oodel SCORE public directory — /oodelscore (top-level nav item)
// ---------------------------------------------------------------------------
export function CustomerSentimentLanding() {
  const T = useT();
  const [rows, setRows] = useState(null);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [leadOpen, setLeadOpen] = useState(false);

  useEffect(() => { api.get("/cs/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    let cancelled = false;
    api.get("/cs/directory", { params: { category: category || undefined, q: search || undefined } })
      .then((r) => { if (!cancelled) setRows(r.data); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [category, search]);

  return (
    <div className="min-h-screen bg-[#0B1424]" data-testid="cs-landing-page">
      <OuterNav />
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="overline text-[#E9C767] mb-3">{T("nav_label_customer_sentiment", "oodel SCORE")}™</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4 text-white">{T("cs_landing_hero_title", "Real customer sentiment, verified by oodel.")}</h1>
        <p className="text-sm md:text-base text-white/50 max-w-2xl mx-auto leading-relaxed">
          {T("cs_score_explainer", "oodel SCORE™ (Sentiment Captured On Real Experiences) is a trusted measure of customer sentiment based on real customer experiences. Every SCORE is calculated from structured feedback submitted after a visit, helping businesses and customers understand genuine customer perception over time.")}
        </p>
        <Button onClick={() => setLeadOpen(true)} className="rounded-full bg-[#E9C767] hover:bg-[#dcb955] text-[#0B1424] h-11 px-7 font-bold mt-6" data-testid="cs-landing-cta">{T("cs_landing_cta_label", "Get your business listed →")}</Button>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search business name…" data-testid="cs-directory-search" className="rounded-full bg-white/5 border-white/10 text-white pl-10 placeholder:text-white/30" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setCategory("")} data-testid="cs-directory-filter-all" className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${category === "" ? "bg-[#E9C767] text-[#0B1424] border-[#E9C767]" : "border-white/15 text-white/50 hover:border-white/40"}`}>All</button>
          {categories.map((c) => (
            <button key={c.slug} onClick={() => setCategory(c.slug)} data-testid={`cs-directory-filter-${c.slug}`} className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${category === c.slug ? "bg-[#E9C767] text-[#0B1424] border-[#E9C767]" : "border-white/15 text-white/50 hover:border-white/40"}`}>{c.name}</button>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-20">
        {rows === null && <div className="text-center text-white/30 py-16 text-sm">Loading businesses…</div>}
        {rows && rows.length === 0 && <div className="text-center text-white/30 py-16 text-sm" data-testid="cs-directory-empty">No businesses found.</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rows && rows.map((b) => (
            <Link to={`/cs/${b.business_slug}`} key={b.business_slug} data-testid={`cs-directory-card-${b.business_slug}`} className="bg-white/[0.04] border border-white/10 rounded-3xl p-6 flex flex-col items-center text-center hover:border-[#E9C767]/50 hover:bg-white/[0.06] transition-colors">
              {b.logo_url ? (
                <img src={b.logo_url} alt="" className="w-14 h-14 rounded-full object-cover mb-3 border border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#E9C767]/10 text-[#E9C767] flex items-center justify-center mb-3"><Storefront size={22} weight="fill" /></div>
              )}
              <div className="font-bold text-white text-lg leading-tight">{b.business_name}</div>
              <div className="text-xs text-white/40 mt-0.5 mb-4 flex items-center gap-2">
                {b.industry && <span>{b.industry}</span>}
                {b.location_count > 1 && <span className="px-1.5 py-0.5 rounded-full bg-white/10 text-white/50 text-[10px] font-bold">{b.location_count} LOCATIONS</span>}
              </div>
              <ScoreBadge avgStars={b.avg_stars} size="sm" testid={`cs-directory-badge-${b.business_slug}`} />
              <ReviewCount totalResponses={b.total_responses} className="mt-2" />
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-black/40 border-t border-white/5 py-12 text-center px-6">
        <h2 className="text-2xl font-bold mb-2 text-white">{T("cs_directory_footer_title", "Already listed on oodel SCORE?")}</h2>
        <p className="text-white/40 mb-5 text-sm">{T("cs_directory_footer_subtitle", "Sign in to view your live dashboard.")}</p>
        <Link to="/login"><Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white hover:text-black" data-testid="cs-landing-business-login">{T("cs_directory_login_label", "Business login →")}</Button></Link>
      </section>

      <LeadFormModal open={leadOpen} onClose={() => setLeadOpen(false)} />
    </div>
  );
}

